import React, { useContext, useMemo, useEffect } from 'react'
import { MAPPING, mappingTabs } from '../Constants/Constants';
import Tab from 'Components/DesignSystem/Tab/Tab';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Divider from 'Components/DesignSystem/Divider/Divider';
import { MappingContext, MappingContextType } from '../Contexts/MappingContext';
import AttributeMappingTable from './AttributeMappingTable';
import AttributeValueMappingTable from './AttributeValueMappingTable';
import CategoryMappingTable from './CategoryMappingTable';
import SearchAndActions from './SearchAndActions';
import StatsPills from './StatsPills';
import SelectedFilters from './SelectedFilters';
import AttributeSelectorList from './AttributeSelectorList';

const MappingTabView = () => {
    const {
        activeTab,
        onTabChange,
        mappingState,
        selectedAttribute,
        setSelectedAttribute,
        pagination,
    } = useContext<MappingContextType>(MappingContext);

    const tabLabels: Record<MappingTabType, string> = {
        'attribute': 'Attribute Mapping',
        'attributevalue': 'Attribute Value Mapping',
        'category': 'Category Mapping'
    };

    // Auto-select first attribute when switching to attributevalue tab
    useEffect(() => {
        if (activeTab === 'attributevalue' && !selectedAttribute) {
            const attributes = mappingState.working.attributes;
            if (attributes && attributes.length > 0) {
                setSelectedAttribute(attributes[0]);
            }
        }
    }, [activeTab, selectedAttribute, setSelectedAttribute, mappingState.working.attributes]);

    // Calculate statistics from API pagination data
    const attributeStats = useMemo(() => {
        const totalCount = pagination.attributes.totalItems || 0;
        const uniqueMapped = pagination.attributes.uniqueMappedCount || 0;
        const pendingReview = pagination.attributes.pendingReviewCount || 0;

        return [
            { count: totalCount, label: "Incoming Attributes" },
            { count: uniqueMapped, label: "Unique Mapped Attributes" },
            { count: pendingReview, label: "Pending Review" }
        ];
    }, [pagination.attributes]);

    const attributeValueStats = useMemo(() => {
        const totalCount = pagination.attributeValues.totalItems || 0;
        // The unique-mapped count is fetched on its own endpoint in parallel
        // with the data page (so the ~3s count never blocks the table). Show a
        // "…" placeholder while that fetch is in flight.
        const uniqueMapped: number | string = pagination.attributeValues.uniqueMappedCountLoading
            ? "…"
            : (pagination.attributeValues.uniqueMappedCount || 0);
        const pendingReview = pagination.attributeValues.pendingReviewCount || 0;

        return [
            { count: totalCount, label: "Incoming Attribute Values" },
            { count: uniqueMapped, label: "Unique Mapped Attribute Values" },
            { count: pendingReview, label: "Pending Review" }
        ];
    }, [pagination.attributeValues]);

    const categoryStats = useMemo(() => {
        const totalCount = pagination.categories.totalItems || 0;
        const uniqueMapped = pagination.categories.uniqueMappedCount || 0;
        const pendingReview = pagination.categories.pendingReviewCount || 0;

        return [
            { count: totalCount, label: "Incoming Categories" },
            { count: uniqueMapped, label: "Unique Mapped Categories" },
            { count: pendingReview, label: "Pending Review" }
        ];
    }, [pagination.categories]);

    // Get current tab stats
    const getCurrentStats = () => {
        switch (activeTab) {
            case 'attribute':
                return { stats: attributeStats, title: 'Attribute' };
            case 'attributevalue':
                return { stats: attributeValueStats, title: 'Attribute Value' };
            case 'category':
                return { stats: categoryStats, title: 'Category' };
            default:
                return { stats: attributeStats, title: 'Attribute' };
        }
    };

    const { stats, title } = getCurrentStats();

    return (
        <>
            <div className='tw-w-full'>
                {/* heading-2 + as="h1" is the enforced app-wide page-title pairing;
                    the ink class is explicit because heading variants are sizes,
                    not inks — without it the title is invisible in dark mode. */}
                <Typography
                    variant="display"
                    as="h1"
                    id="mapping-title"
                    data-testid="mapping-title"
                    className="tw-text-left tw-text-ink tw-mb-4 tw-mt-1"
                >
                    {MAPPING}
                </Typography>
            </div>
            <Divider />

            <div className="tw-flex tw-justify-between tw-items-end" data-testid="mapping-tabs">
                <Tab
                    tabs={mappingTabs.map((tab) => ({
                        id: tab,
                        label: tabLabels[tab],
                        'data-testid': `tab-${tab}`,
                    }))}
                    activeTabId={activeTab}
                    onChange={(tabId) => onTabChange(tabId as MappingTabType)}
                />
            </div>
            <Divider />

            {activeTab === 'attributevalue' ? (
                <div className="tw-flex tw-gap-4 tw-mt-3">
                    {/* Left Column: Attribute Selector */}
                    <div className="tw-flex-shrink-0">
                        <AttributeSelectorList />
                    </div>

                    {/* Right Column: Stats, Search, and Table.

                        § 325 — `tw-min-w-0` for the same reason as the search wrapper
                        inside `SearchAndActions`: without it this column cannot shrink
                        below its content's min-content width, so anything wide in here
                        propagates straight out to the page — or, when this module is
                        embedded in the Missing Mapping side panel (66vw), to the panel,
                        which then scrolls horizontally. This is the durable half of the
                        fix: the search row is narrow again either way, but without this
                        the next wide thing added to this column reopens the bug. */}
                    <div className="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col">
                        <StatsPills
                            title={title}
                            pills={stats}
                            className="tw-mb-3"
                        />
                        <Divider className="tw-mb-1.5" />
                        <SearchAndActions />
                        <Divider className="tw-mt-1.5" />
                        <SelectedFilters />
                        <AttributeValueMappingTable />
                    </div>
                </div>
            ) : (
                <>
                    <StatsPills
                        title={title}
                        pills={stats}
                        className="tw-mb-3"
                    />
                    <Divider />
                    <SearchAndActions />
                    <Divider />
                    <SelectedFilters />

                    {activeTab === 'attribute' && <AttributeMappingTable />}
                    {activeTab === 'category' && <CategoryMappingTable />}
                </>
            )}
        </>
    )
}

export default MappingTabView
